import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, debounceTime, distinctUntilChanged, map, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { EXCLUDED_COUNTRIES } from 'src/app/core/const.variables';
import { slideInOut } from '../../animations/slide-in-out.animation';
import { ApiService } from 'src/app/core/services/api.service';
import { opacityInOut } from '../../animations/opacity-in-out.animation';
import { getWindow } from 'ssr-window';

@Component({
  selector: 'a-select-company',
  templateUrl: 'a-select-company.component.html',
  styleUrls: ['a-select-company.component.scss'],
  animations: [slideInOut, opacityInOut]
})
export class ASelectCompanyComponent implements OnInit, OnDestroy {

  @ViewChild('scrolledEl') scrolledEl: ElementRef | undefined;
  @ViewChild('selectEl') selectEl: ElementRef | undefined;
  @Input() isSubmitted = true;
  @Input() label = '';
  @Input() grayBg = true;
  @Input() isOptional = false;
  @Input() isDisabled = false;
  @Input() control: FormGroup = this.fb.group({
    id: null,
    name: ['', [Validators.required]],
    country: null
  });
  @Output() onBlur = new EventEmitter<any>();
  
  destroy$ = new Subject<boolean>();
  searchInput$ = new BehaviorSubject<string | null>('');
  searchVariants = [];
  loadingResults = false;
  showItems = false;
  inputFocused = false;
  openPositionTop = false;
  selectedItem;
  searchValue = '';
  showLangNotify = false;
  wasInited = false;

  constructor(private apiService: ApiService, private fb: FormBuilder) {
  }

  ngOnInit(): void {
    if (this.control.get('name').value) {
      this.initState(this.control.get('name').value);
    } else if (!this.wasInited) {
      this.control.get('name').valueChanges.pipe(take(1)).subscribe(value => {
        this.initState(value);
      })
    }
    this.searchInput$.pipe(
      takeUntil(this.destroy$),
      map(searchTerm => {
        if (searchTerm && searchTerm.length > 2) {
          this.loadingResults = true;
        }
        return searchTerm;
      }),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(input => {
        if (input && input.length > 2) {
          return this.getCompanies(input)
        } else {
          this.loadingResults = false;
          return of([]);
        }
      })
    ).subscribe(res => {
      this.searchVariants = res;
    });
    this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.wasInited = true;
      this.checkLocation(val);
      if (val.name && val.name.length && val.name.length > 2 && val.country && !val.country.includes(EXCLUDED_COUNTRIES)) {
        this.checkCompanyId();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onFocus() {
    this.showDropdown();
  }

  onBlurAction() {
    if (this.searchValue !== this.control.get('name').value) {
      this.control.get('name').setValue(this.searchValue);
    }
  }

  showDropdown(val: boolean = true) {
    this.showItems = val;
    if (this.selectEl) {
      const position = this.selectEl.nativeElement.getBoundingClientRect().top;
      this.openPositionTop = getWindow().innerHeight - position < 240;
    }
  }

  selectItem(val: any) {
    if (val) {
      this.searchValue = val.name;
      this.selectedItem = val;
      this.showDropdown(false);
      this.control.patchValue({
        id: val.id,
        name: val.name,
        country: val.location.country
      });
      this.onBlur.emit();
    }
  }

  findResult(val: any) {
    this.searchValue = val;
    this.searchInput$.next(val);
  }

  hideNotify() {
    this.showLangNotify = false;
  }

  inputValidator(event: any) {
    const pattern = /^[a-zA-Z0-9\s._-]*$/;
    if (!pattern.test(event.target.value)) {
      event.target.value = event.target.value.replace(/[^a-zA-Z0-9\s._-]/g, "");
      this.showLangNotify = true;
    } else {
      this.findResult(event.target.value);
      this.selectedItem = null;
    }
  }

  private initState(value) {
    if (value) {
      this.findResult(value);
      this.checkLocation({ name: value })
      this.selectedItem = { 
        name: value ,
        location: { country: this.control.get('country').value }
      }
    }
  }

  private getCompanies(input): Observable<any> {
    this.loadingResults = true;
    return this.apiService.getCompaniesList(input).pipe(map(res => {
      this.loadingResults = false;
      return res ? res : [];
    }));
  }

  private checkCompanyId() {
    const name = this.control.get('name').value;
    const location = this.control.get('country').value;
    this.control.setErrors({ processing: true }, { emitEvent: false });
    this.apiService.getCompaniesList(name, location).subscribe(res => {
      const id = res && res.length ? res[0].id : null;
      this.selectedItem = id ? res[0] : null;
      this.control.get('id').setValue(id, { emitEvent: false });
      this.control.setErrors(null, { emitEvent: false });
    });
  }

  private checkLocation(val) {
    setTimeout(() => {
      if (val.name && val.name.length && val.name.length > 2) {
        this.control.get('country').enable({ onlySelf: true, emitEvent: false });
      } else {
        this.control.get('country').setValue('', { onlySelf: true });
        this.control.get('country').disable({ onlySelf: true, emitEvent: false });
      }
    });
  }
}
